import axios from "axios";
import { BACKEND_URL } from "../../utils/informationUtility";
import { handleLogoutTokenExpired } from "../../utils/utilityFunctions";

// user api
const API = axios.create({
  baseURL: "/api/user",
});

// register
const register = async (userData) => {
  const { first_name, last_name, email, organization_name, neo_session_id } = userData;

  // 1) Required fields
  if (!first_name || !first_name.trim()) {
    return Promise.reject(new Error("First name is required"));
  }
  if (!last_name || !last_name.trim()) {
    return Promise.reject(new Error("Last name is required"));
  }
  if (!email || !email.trim()) {
    return Promise.reject(new Error("Email is required"));
  }
  if (!organization_name || !organization_name.trim()) {
    return Promise.reject(new Error("Organization name is required"));
  }

  try {
    // Build request payload - only include neo_session_id if it exists
    const requestPayload = { ...userData };
    if (neo_session_id) {
      requestPayload.neo_session_id = neo_session_id;
    }

    const response = await API.post("/register", requestPayload);

    if (response.data) {
      const { token, ...user } = response.data;
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("token", JSON.stringify(token));
      return { user };
    } else {
      // Server responded with no data
      return Promise.reject(
        new Error(response.error || "Registration failed.")
      );
    }
  } catch (error) {
    // Optionally log the error
    console.error("Registration error:", error);
    // Reject so caller can handle/display
    return Promise.reject(new Error(error.response.data.error));
  }
};

// dev function to update user privilige level -> change to -> update user details
const updateUserPersonal = async ({ data, token }) => {
  // token validation
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { user_id, userDetails } = data;

  // ensure at least one field to update
  const keys = Object.keys(userDetails || {});
  if (keys.length === 0) {
    throw new Error(
      "Request body is required with at least one field to update"
    );
  }

  // validate allowed fields
  const validFields = ["first_name", "last_name"];
  const invalid = keys.filter((f) => !validFields.includes(f));
  if (invalid.length) {
    throw new Error(`Invalid fields: ${invalid.join(", ")}`);
  }

  // first_name must be non-empty string
  if ("first_name" in userDetails) {
    if (
      typeof userDetails.first_name !== "string" ||
      userDetails.first_name.trim().length === 0
    ) {
      throw new Error("First name must be a non-empty string");
    }
  }

  // last_name must be non-empty string
  if ("last_name" in userDetails) {
    if (
      typeof userDetails.last_name !== "string" ||
      userDetails.last_name.trim().length === 0
    ) {
      throw new Error("Last name must be a non-empty string");
    }
  }

  try {
    const response = await API.post(`edit/${user_id}`, userDetails, config);
    return response.data;
  } catch (error) {
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    const backendMsg = error.response?.data?.message;
    throw new Error(backendMsg || "Network Error");
  }
};

const getUserByID = async (id, token) => {
  // token validation
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (!id || typeof id !== "string" || id.trim() === "") {
    throw new Error("Invalid user ID format.");
  }

  try {
    const response = await API.get(`/${id}`, config);
    // response.data is { user: { … } } on 200
    return response.data;
  } catch (error) {
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    if (error.response) {
      // first try data.message, then data.status, then fallback to error.message
      const data = error.response.data || {};
      const msg = data.message || data.status || error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

const getUser = async (id, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(`/${id}`, config);
    return response.data;
  } catch (error) {
    return error;
  }
};
// login
export const login = async ({ email, password, neo_session_id }) => {
  // email presence & format
  if (!email || !email.trim()) {
    return Promise.reject(new Error("Email is required"));
  }
  if (typeof email !== "string" || !email.includes("@")) {
    return Promise.reject(new Error("Invalid email format"));
  }

  // password presence & length
  if (!password || !password.trim()) {
    return Promise.reject(new Error("Password is required"));
  }
  if (typeof password !== "string" || password.length < 8) {
    return Promise.reject(new Error("Password must be at least 8 characters"));
  }
  
  
  try {
    // Build request payload - only include neo_session_id if it exists
    const requestPayload = { email, password };
    if (neo_session_id) {
      requestPayload.neo_session_id = neo_session_id;
    }

    const response = await API.post(
      "/login",
      requestPayload,
      { headers: { "Content-Type": "application/json" } }
    );

    // store user + token
    localStorage.setItem("user", JSON.stringify(response.data));
    localStorage.setItem("token", JSON.stringify(response.data.token));

    return {
      user: response.data,
      token: response.data.token,
    };
  } catch (error) {
    const backendMsg = error.response?.data?.message;
    return Promise.reject(new Error(backendMsg || "Login failed"));
  }
};

// generate verificaion token
const generateVerificationToken = async (data) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (
    !data.email ||
    typeof data.email !== "string" ||
    data.email.trim() === ""
  ) {
    throw new Error("Invalid email format.");
  }

  // Check that data.email matches the logged‐in user’s email
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const currentEmail = storedUser.email;
  if (data.email !== currentEmail) {
    throw new Error(
      "You don't have permission to create a token for another email"
    );
  }

  try {
    const response = await API.post(
      "/generate/verification_token",
      { email: data.email },
      config
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    const backendMsg = error.response?.data?.message;
    throw new Error(backendMsg || "Failed to generate verification token");
  }
};

// delete verificaion token
const deleteVerificationToken = async (data) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (
    !data.email ||
    typeof data.email !== "string" ||
    data.email.trim() === ""
  ) {
    throw new Error("Invalid email format.");
  }

  // Check that data.email matches the logged‐in user’s email
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const currentEmail = storedUser.email;
  if (data.email !== currentEmail) {
    throw new Error(
      "You don't have permission to create a token for another email"
    );
  }

  try {
    const response = await API.post(
      "/delete/verification_token",
      { email: data.email },
      config
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    const backendMsg = error.response?.data?.message;
    throw new Error(backendMsg || "Failed to delete verification token");
  }
};

const resetUserPassword = async ({ user_email, password }) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // validate user_email presence & format
  if (
    !user_email ||
    typeof user_email !== "string" ||
    user_email.trim() === ""
  ) {
    throw new Error("Email is required");
  }
  const emailRegex = /^[^@]+@[^@]+\.[^@]+$/;
  if (!emailRegex.test(user_email)) {
    throw new Error("Invalid email format");
  }

  // validate password presence & length ≥ 8
  if (!password || typeof password !== "string") {
    throw new Error("Password is required");
  }
  if (password.length < 8) {
    throw new Error("Password must be at least 8 characters long");
  }

  // check current user privilege
  const currentUser = JSON.parse(localStorage.getItem("user"));
  if (!currentUser || !currentUser.privilege) {
    throw new Error("Authentication required with valid organization context.");
  }
  if (!["admin", "owner"].includes(currentUser.privilege)) {
    throw new Error("Insufficient privileges to delete organization.");
  }

  try {
    const response = await API.post(
      `/edit_user_password/${user_email}`,
      { password },
      config
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    const backendMsg = error.response?.data?.message;
    throw new Error(backendMsg || "Failed to reset password");
  }
};

//Update updateFirstTimeUser
const updateFirstTimeUser = async ({ userDetails }) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const { user_id, status } = userDetails;
  // input validation
  if (!user_id || typeof user_id !== "string" || user_id.trim() === "") {
    throw new Error("Invalid user ID format.");
  }
  if (!("status" in userDetails) || typeof status !== "boolean") {
    throw new Error("Status must be a boolean value.");
  }

  try {
    const response = await API.post(
      `/update_first_time_user/${user_id}`,
      { status },
      config
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    const backendMsg = error.response?.data?.message;
    throw new Error(backendMsg || "Failed to update user status");
  }
};

const getUserBotMemory = async (data) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  const { user_id, bot_id } = data;

  if (!user_id || typeof user_id !== "string" || user_id.trim() === "") {
    throw new Error("Invalid user ID format.");
  }
  if (!bot_id || typeof bot_id !== "string" || bot_id.trim() === "") {
    throw new Error("Invalid bot ID format.");
  }

  try {
    const response = await API.get(`memory/${user_id}/${bot_id}`, config);
    if (!response.status.toString().startsWith("2")) {
      throw new Error(`Unexpected response code: ${response.status}`);
    }
    return response.data;
  } catch (error) {
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    const backendMsg = error.response?.data?.message;
    throw new Error(
      backendMsg || error.message || "Failed to fetch bot memory"
    );
  }
};

const getOTP = async (data, token) => {
  const config = {
    headers: {
      "Content-Type": "application/json",
    },
  };
  const { email, first_name } = data;
  // input validation
  if (!email || typeof email !== "string" || email.trim() === "") {
    throw new Error("Invalid email format.");
  }
  if (
    typeof first_name !== "string" 
  ) {
    throw new Error("First name is required.");
  }

  try {
    const response = await API.post(
      `/send_invitation_email/${email}`,
      { first_name: first_name },
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching bot memory:", error);
    throw error;
  }
};

const forgetPassword = async (data, token) => {
  const { email, password } = data;

  // input validation
  if (!email || typeof email !== "string" || email.trim() === "") {
    throw new Error("Invalid email format.");
  }
  if (!password || typeof password !== "string" || password.trim() === "") {
    throw new Error("Password is required.");
  }

  try {
    const response = await API.post(
      `/user/reset_forgot_password/${email}`,
      { password: password },
      {}
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    console.error("Error fetching bot memory:", error);
    throw error;
  }
};

//bugReport
const _bugReport_ = async (data, token) => {
  // token validation
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  // input validation
  if (!data || typeof data !== "object") {
    throw new Error("Invalid data format.");
  }
  if (
    !data.user_id ||
    typeof data.user_id !== "string" ||
    data.user_id.trim() === ""
  ) {
    throw new Error("Invalid user ID format.");
  }

  try {
    const response = await API.post(
      `/user/bug-report/${data.user_id}`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    console.error("Error sending bug report:", error);
    throw error;
  }
};

const createNewMemorySession = async (user_id, bot_id, sessionTitle, token) => {
  // token validation
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  // input validation
  if (!user_id || typeof user_id !== "string" || user_id.trim() === "")
    throw new Error("Invalid user ID format.");
  if (!bot_id || typeof bot_id !== "string" || bot_id.trim() === "")
    throw new Error("Invalid bot ID format.");
  if (
    !sessionTitle ||
    typeof sessionTitle !== "string" ||
    sessionTitle.trim() === ""
  )
    throw new Error(
      "Session title is required and must be a non-empty string."
    );

  try {
    const response = await API.post(
      `/create/session/${user_id}/${bot_id}`,
      { query: sessionTitle },
      config
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    console.error("Error creating new memory session:", error);
    throw error;
  }
};

const getSession = async (session_id, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(`/session/${session_id}`, config);
    if (!response.status.toString().startsWith("2")) {
      throw new Error(`Unexpected response code: ${response.status}`);
    }
    return response.data;
  } catch (error) {
    console.error("Error fetching session:", error);
    throw error;
  }
};

const deleteSession = async (session_id, token) => {
  // token validation
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (
    !session_id ||
    typeof session_id !== "string" ||
    session_id.trim() === ""
  ) {
    throw new Error("Invalid session ID format.");
  }

  try {
    const response = await API.delete(`/delete/session/${session_id}`, config);
    if (!response.status.toString().startsWith("2")) {
      throw new Error(`Unexpected response code: ${response.status}`);
    }
    return { session_id };
  } catch (error) {
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    console.error("Error deleting session:", error);
    throw error;
  }
};

const UpdateCurrentUserPassword = async (data) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  const email = data.user_email;
  const dataObj = {
    oldPassword: data.oldPassword,
    password: data.password,
    email,
  };

  // input validation
  if (!email || typeof email !== "string" || email.trim() === "") {
    throw new Error("Invalid email format.");
  }
  if (!dataObj.oldPassword || typeof dataObj.oldPassword !== "string") {
    throw new Error("Old password is required.");
  }
  if (!dataObj.password || typeof dataObj.password !== "string") {
    throw new Error("New password is required.");
  }

  try {
    const response = await API.post("/reset_password", dataObj, config);
    return response.data;
  } catch (error) {
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    const backendMsg = error.response?.data?.message;
    throw new Error(backendMsg || "Failed to reset password");
  }
};

// Create public demo session
const createPublicDemoSession = async () => {
  const response = await API.get("/quick-bot/create-public-session");
  if (response.data) {
    localStorage.setItem("user", JSON.stringify(response.data));
    localStorage.setItem("token", JSON.stringify(response.data.token));
    return {
      user: response.data,
      token: response.data.token,
    };
  } else {
    throw new Error(response.error || "Demo session creation failed.");
  }
};

export const getGoogleLoginUrl = () =>
  `${BACKEND_URL}/api/user/google/login?google_auth=true`;

export const getGoogleRegisterUrl = () =>
  `${BACKEND_URL}/api/user/google/register`;

const authService = {
  register,
  login,
  getUserByID,
  getUser,
  updateUserPersonal,
  generateVerificationToken,
  deleteVerificationToken,
  resetUserPassword,
  getUserBotMemory,
  getOTP,
  forgetPassword,
  updateFirstTimeUser,
  _bugReport_,
  createNewMemorySession,
  getSession,
  deleteSession,
  UpdateCurrentUserPassword,
  getGoogleLoginUrl,
  getGoogleRegisterUrl,
  createPublicDemoSession,
};

export default authService;
